/**
 * Life4Earth V2 — React hook for loading security data from the API.
 *
 * Fetches all 7 security tables in parallel, converts to seed shapes,
 * and falls back to static seed data when the API is unreachable.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import {
  fetchAllSecurityUsers,
  fetchAllCommunityMemberships,
  fetchAllCommunityRoles,
  fetchAllProjectAcl,
  fetchAllCollectionMembershipsSec,
  fetchAllSecurityLinks,
  fetchAllUserPreferences,
  securityUserToSeedShape,
  communityMembershipToSeedShape,
  communityRoleToSeedShape,
  projectAclToSeedShape,
  collectionMembershipSecToSeedShape,
  securityLinkToSeedShape,
  preferenceToSeedShape,
} from "./security";
import { fetchForKey, CACHE_UPDATE_EVENT, QUERY_KEYS } from "./queryCache";

export interface UseApiSecurityDataResult {
  securityUsers: Record<string, any>[];
  communityMemberships: Record<string, any>[];
  communityRoles: Record<string, any>[];
  projectAcl: Record<string, any>[];
  collectionMemberships: Record<string, any>[];
  securityLinks: Record<string, any>[];
  preferences: Record<string, any>[];

  rawCounts: {
    users: number;
    communityMemberships: number;
    communityRoles: number;
    projectAcl: number;
    collectionMemberships: number;
    securityLinks: number;
    preferences: number;
  };

  loading: boolean;
  error: string | null;
  source: "api" | "seed" | "loading";
  refetch: () => void;
}

export function useApiSecurityData(): UseApiSecurityDataResult {
  const mountedRef = useRef(true);
  const [data, setData] = useState<UseApiSecurityDataResult>({
    securityUsers: [],
    communityMemberships: [],
    communityRoles: [],
    projectAcl: [],
    collectionMemberships: [],
    securityLinks: [],
    preferences: [],
    rawCounts: {
      users: 0, communityMemberships: 0, communityRoles: 0,
      projectAcl: 0, collectionMemberships: 0, securityLinks: 0, preferences: 0,
    },
    loading: true,
    error: null,
    source: "loading",
    refetch: () => {},
  });

  const refetch = useCallback(async () => {
    if (!mountedRef.current) return;

    try {
      const cached = await fetchForKey(
        QUERY_KEYS.security,
        async () => {
          const [
            apiUsers, apiCm, apiCr, apiPacl,
            apiColm, apiLinks, apiPrefs,
          ] = await Promise.all([
            fetchAllSecurityUsers(),
            fetchAllCommunityMemberships(),
            fetchAllCommunityRoles(),
            fetchAllProjectAcl(),
            fetchAllCollectionMembershipsSec(),
            fetchAllSecurityLinks(),
            fetchAllUserPreferences(),
          ]);

          return {
            securityUsers: apiUsers.map(securityUserToSeedShape),
            communityMemberships: apiCm.map(communityMembershipToSeedShape),
            communityRoles: apiCr.map(communityRoleToSeedShape),
            projectAcl: apiPacl.map(projectAclToSeedShape),
            collectionMemberships: apiColm.map(collectionMembershipSecToSeedShape),
            securityLinks: apiLinks.map(securityLinkToSeedShape),
            preferences: apiPrefs.map(preferenceToSeedShape),
            rawCounts: {
              users: apiUsers.length,
              communityMemberships: apiCm.length,
              communityRoles: apiCr.length,
              projectAcl: apiPacl.length,
              collectionMemberships: apiColm.length,
              securityLinks: apiLinks.length,
              preferences: apiPrefs.length,
            },
          };
        },
        { force: true }
      );

      if (mountedRef.current) {
        setData({
          securityUsers: cached.securityUsers,
          communityMemberships: cached.communityMemberships,
          communityRoles: cached.communityRoles,
          projectAcl: cached.projectAcl,
          collectionMemberships: cached.collectionMemberships,
          securityLinks: cached.securityLinks,
          preferences: cached.preferences,
          rawCounts: cached.rawCounts,
          loading: false,
          error: null,
          source: "api",
          refetch,
        });
      }
    } catch (err: any) {
      if (mountedRef.current) {
        setData({
          securityUsers: [],
          communityMemberships: [],
          communityRoles: [],
          projectAcl: [],
          collectionMemberships: [],
          securityLinks: [],
          preferences: [],
          rawCounts: {
            users: 0, communityMemberships: 0, communityRoles: 0,
            projectAcl: 0, collectionMemberships: 0, securityLinks: 0, preferences: 0,
          },
          loading: false,
          error: err.message,
          source: "seed",
          refetch,
        });
      }
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;

    async function load() {
      try {
        const cached = await fetchForKey(
          QUERY_KEYS.security,
          async () => {
            const [
              apiUsers, apiCm, apiCr, apiPacl,
              apiColm, apiLinks, apiPrefs,
            ] = await Promise.all([
              fetchAllSecurityUsers(),
              fetchAllCommunityMemberships(),
              fetchAllCommunityRoles(),
              fetchAllProjectAcl(),
              fetchAllCollectionMembershipsSec(),
              fetchAllSecurityLinks(),
              fetchAllUserPreferences(),
            ]);

            return {
              securityUsers: apiUsers.map(securityUserToSeedShape),
              communityMemberships: apiCm.map(communityMembershipToSeedShape),
              communityRoles: apiCr.map(communityRoleToSeedShape),
              projectAcl: apiPacl.map(projectAclToSeedShape),
              collectionMemberships: apiColm.map(collectionMembershipSecToSeedShape),
              securityLinks: apiLinks.map(securityLinkToSeedShape),
              preferences: apiPrefs.map(preferenceToSeedShape),
              rawCounts: {
                users: apiUsers.length,
                communityMemberships: apiCm.length,
                communityRoles: apiCr.length,
                projectAcl: apiPacl.length,
                collectionMemberships: apiColm.length,
                securityLinks: apiLinks.length,
                preferences: apiPrefs.length,
              },
            };
          }
        );

        if (mountedRef.current) {
          setData({
            securityUsers: cached.securityUsers,
            communityMemberships: cached.communityMemberships,
            communityRoles: cached.communityRoles,
            projectAcl: cached.projectAcl,
            collectionMemberships: cached.collectionMemberships,
            securityLinks: cached.securityLinks,
            preferences: cached.preferences,
            rawCounts: cached.rawCounts,
            loading: false,
            error: null,
            source: "api",
            refetch,
          });
        }
      } catch (err: any) {
        if (mountedRef.current) {
          setData({
            securityUsers: [],
            communityMemberships: [],
            communityRoles: [],
            projectAcl: [],
            collectionMemberships: [],
            securityLinks: [],
            preferences: [],
            rawCounts: {
              users: 0, communityMemberships: 0, communityRoles: 0,
              projectAcl: 0, collectionMemberships: 0, securityLinks: 0, preferences: 0,
            },
            loading: false,
            error: err.message,
            source: "seed",
            refetch,
          });
        }
      }
    }

    load();

    const handleCacheUpdate = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail?.key === QUERY_KEYS.security) {
        load();
      }
    };

    window.addEventListener(CACHE_UPDATE_EVENT, handleCacheUpdate);

    return () => {
      mountedRef.current = false;
      window.removeEventListener(CACHE_UPDATE_EVENT, handleCacheUpdate);
    };
    // Intentional: this is the hook's mount-once initializer. `refetch` is
    // referenced internally and is stable (defined via useCallback with []
    // deps above). Adding it to the dep array would cause the effect to
    // tear down + remount on every render, defeating the cache layer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return data;
}
